<script>
	import Image from '$lib/Components/Layout/Image/Image.svelte';
	import Section from '$lib/Components/Layout/Section/Section.svelte';
	import ScrollNav from '$lib/Components/ScrollNav/ScrollNav.svelte';
	import Youtube from '$lib/Components/Youtube/Youtube.svelte';

	import headerImage from './images/image-01.jpg';
	import identifyFrameStep from './images/image-03.png';
	import measureTape from './images/image-04.png';
	import topBottomHeightA from './images/image-05a.svg';
	import topBottomHeightB from './images/image-05b.svg';
	import leftRightHeightA from './images/image-06a.svg';
	import leftRightHeightB from './images/image-06b.svg';
	import leftRightDiagonalA from './images/image-07a.svg';
	import leftRightDiagonalB from './images/image-07b.svg';
	import bowedFrameA from './images/image-08a.svg';
	import bowedFrameB from './images/image-08b.svg';
	import gapValue from './images/image-09.jpg';
	import measureForm from './images/image-10.jpg';
	import youtubeThumbnail from './images/youtubeThumbnail.jpg?w=610';
</script>

<Section
	backgroundImage={headerImage}
	backgroundColor="blue"
	loading="eager"
	class="
   min-h-[clamp(20rem,66dvh,33dvw)]
   max-md:-mt-32
 "
>
	<h1 class="self-end">How To Measure Using Tape</h1>
</Section>

<Section backgroundColor="grey-light" class="[&_p]:text-left">
	<p>
		Indow customers who opt for the <strong> Tape Measure Program </strong>do so
		because they want to achieve comfort and quiet in their space as soon as
		possible. Get started today using the instructions below.
	</p>
	<p>
		If we missed something let us know: <a href="tel:5032842260">503.284.2260</a
		>
	</p>
</Section>
<ScrollNav>
	<li>
		<!-- eslint-disable-next-line better-tailwindcss/no-unknown-classes -->
		<a href="#prepare-to-measure" class="scrollactive-item"
			>Prepare to Measure</a
		>
	</li>
	<li>
		<!-- eslint-disable-next-line better-tailwindcss/no-unknown-classes -->
		<a href="#accessing-each-window" class="scrollactive-item"
			>Accessing Each Window</a
		>
	</li>
	<li>
		<!-- eslint-disable-next-line better-tailwindcss/no-unknown-classes -->
		<a href="#measure-widths-and-heights" class="scrollactive-item"
			>Measure Widths & Heights</a
		>
	</li>
	<li>
		<!-- eslint-disable-next-line better-tailwindcss/no-unknown-classes -->
		<a href="#measure-diagonals" class="scrollactive-item">Measure Diagonals</a>
	</li>
	<li>
		<!-- eslint-disable-next-line better-tailwindcss/no-unknown-classes -->
		<a href="#final-window-check" class="scrollactive-item"
			>Final Window Check</a
		>
	</li>
	<li>
		<!-- eslint-disable-next-line better-tailwindcss/no-unknown-classes -->
		<a href="#submit-measurements" class="scrollactive-item"
			>Submit Measurements</a
		>
	</li>
</ScrollNav>

<Section
	id="prepare-to-measure"
	class="
   max-md:[&_h3]:justify-self-start max-md:[&_h3]:text-left
   max-md:[&_h4]:justify-self-start max-md:[&_h4]:text-left
   [&_p]:text-left
 "
>
	<h2>Preparing to Measure</h2>
	<p>
		Remove any furniture around the windows for easy access and a more accurate
		measurement. If you have blinds that have to be removed, now is a good time.
		Get a piece of paper to write your measurements on, or you can download our
		<strong>Measure Form</strong>
		<a href="/static/pdfs/Self_Measure_Form-FOR-TAPE-MEASURE-10-13-21-1.pdf"
			>here</a
		>.
	</p>

	<h3>Record:</h3>
	<ul class="w-fit">
		<li>
			<p>Room and Location (as you want them to appear on the insert label)</p>
		</li>
		<li>
			<p>Product Type (Standard Grade, Acoustic Grade, Film, etc.)</p>
		</li>
		<li>
			<p>Frame Color</p>
		</li>
	</ul>

	<h4>NOTE:</h4>
	<p>
		Measuring a lot of windows? Start with 3 or 4, then enter measurements into
		the <strong>Measure by Indow</strong> portal to make sure they are accepted before
		continuing.
	</p>
</Section>

<Section
	backgroundColor="grey-light"
	id="accessing-each-window"
	class="
   max-md:[&_h3]:justify-self-start max-md:[&_h3]:text-left
   max-md:[&_h4]:justify-self-start max-md:[&_h4]:text-left
   [&_p]:text-left
 "
>
	<h2>Accessing Each Window</h2>
	<h3>FRAME STEP & DEPTH</h3>
	<p>
		<strong>Frame Step</strong> is where your insert will be installed in your
		window frame. <strong>Frame Depth*</strong> is how deep your frame step is.
		For all dimensions, place the metal corner of your measuring tape into the
		corner of your frame step and measure straight across. Recording the step &
		depth on your <strong>Measure Form</strong> will help you measure and install
		in the same place.
	</p>

	<Youtube
		videoId="WPNWItsrqMU"
		posterImageSrc={youtubeThumbnail}
		title="Video: STC Ratings of Materials"
	/>

	<h3>Identify Frame Step</h3>
	<p>
		Some windows have more than one frame step, while other windows have just a
		single, flat surface. This example shows two frame steps. <span
			class="text-indow-blue">(1) in blue</span
		>
		and <span class="text-indow-yellow">(2) in yellow</span>. For each window,
		you will be taking a set of 6 measurements which must be taken on the same
		frame step.
	</p>

	<Image src={identifyFrameStep} class="no-bleed" />

	<h3>A COMPATIBLE FRAME STEP MUST HAVE:</h3>
	<ul class="w-fit">
		<li>
			<p>5/8" of flat space (if measuring for Indow inserts)</p>
		</li>
		<li>
			<p>(11/4" if blinds are installed at top of frame)</p>
		</li>
		<li>
			<p>At least 1/2" of space from window glass</p>
		</li>
		<li>
			<p>No obstructions such as shutters, shades. handles, cranks, or locks</p>
		</li>
	</ul>
	<h4>NOTE:</h4>
	<p>
		For noise reduction, the best frame step is at least 1"-4" away from the
		window glass.
	</p>
</Section>

<Section
	id="measure-widths-and-heights"
	class="
   max-md:[&_h3]:justify-self-start max-md:[&_h3]:text-left
   max-md:[&_h4]:justify-self-start max-md:[&_h4]:text-left
   [&_p]:text-left
 "
>
	<h2>Measure Widths & Heights</h2>

	<Image src={measureTape} class="no-bleed" />

	<h3>A Width Top</h3>
	<p>
		Locate the frame step the inserts will be installed on. Measure top left
		corner to the opposite (top right) side.
	</p>
	<p>
		Write the measurement on your <strong>Measure Form</strong> under the column
		<strong>'A Width Top'</strong>. Record the measurements in fraction. Measure
		Twice!
	</p>
	<div
		class="
    col-span-full! flex w-full flex-col items-center justify-center
    overflow-auto
    sm:flex-row
  "
	>
		<div
			class="
     relative grid w-64 justify-end
     md:w-auto
   "
		>
			<Image
				src={topBottomHeightA}
				class="
      w-48 object-contain
      md:w-68
    "
			/>
			<div
				class="
      absolute top-[16%] left-[5%] w-40
      md:top-[16%] md:left-[-16%] md:w-48
    "
			>
				<div
					class="
       text-[0.67rem] font-bold text-indow-blue
       md:text-sm
     "
				>
					A WIDTH TOP
				</div>
				<div
					class="
       relative h-px w-32 bg-indow-blue
       md:w-40
     "
				>
					<div
						class="
        absolute top-[-50%] right-0 size-3 translate-y-[-50%] transform
        rounded-full bg-inherit
      "
					></div>
				</div>
			</div>
		</div>
		<div
			class="
     relative grid w-64 justify-start
     md:w-auto
   "
		>
			<Image
				src={topBottomHeightB}
				class="
      w-48 object-contain
      md:w-68
    "
			/>
			<div
				class="
      absolute right-[-3%] bottom-[17%]
      md:right-[-28%] md:bottom-[16%]
    "
			>
				<div
					class="
       w-full text-right text-[0.67rem] font-bold text-indow-blue
       md:text-sm
     "
				>
					B WIDTH BOTTOM
				</div>
				<div
					class="
       relative h-px w-36 bg-indow-blue
       md:w-44
     "
				>
					<div
						class="
        absolute top-[-50%] left-0 size-3 translate-y-[-50%] transform
        rounded-full bg-inherit
      "
					></div>
				</div>
			</div>
		</div>
	</div>

	<h3>B Width Bottom</h3>
	<p>
		Use the same frame step as before. Measure from the bottom left corner of
		your window frame across to the right. Record your measurements under <strong
			>'B Width Bottom'</strong
		>
		on the <strong>Measure Form</strong>.
	</p>
	<h3>C Height Left</h3>
	<p>
		Using the same frame step as before, measure from the bottom left of the
		window to the top of the frame's left side. Measure Twice! Write these
		measurements on your <strong>Measure Form</strong> under
		<strong>'C Height Left'</strong>.
	</p>
	<div
		class="
    col-span-full! flex w-full flex-col items-center justify-center
    overflow-auto
    sm:flex-row
  "
	>
		<div
			class="
     relative grid w-64 justify-end
     md:w-auto
   "
		>
			<Image
				src={leftRightHeightA}
				class="
      w-48 object-contain
      md:w-68
    "
			/>
			<div
				class="
      absolute top-[50%] left-2
      md:left-[-29%]
    "
			>
				<div
					class="
       text-[0.67rem] font-bold text-indow-blue
       md:text-sm
     "
				>
					C HEIGHT LEFT
				</div>
				<div
					class="
       relative h-px w-28 bg-indow-blue
       md:w-40
     "
				>
					<div
						class="
        absolute top-[-50%] right-0 size-3 translate-y-[-50%] transform
        rounded-full bg-inherit
      "
					></div>
				</div>
			</div>
		</div>
		<div
			class="
     relative grid w-64 justify-start
     md:w-auto
   "
		>
			<Image
				src={leftRightHeightB}
				class="
      w-48 object-contain
      md:w-68
    "
			/>
			<div
				class="
      absolute top-[50%] right-[2%]
      md:right-[-32%]
    "
			>
				<div
					class="
       w-full text-right text-[0.67rem] font-bold text-indow-blue
       md:text-sm
     "
				>
					D HEIGHT RIGHT
				</div>
				<div
					class="
       relative h-px w-28 bg-indow-blue
       md:w-40
     "
				>
					<div
						class="
        absolute top-[-50%] left-0 size-3 translate-y-[-50%] transform
        rounded-full bg-inherit
      "
					></div>
				</div>
			</div>
		</div>
	</div>

	<h3>D Height Right</h3>
	<p>
		Now do the same height measurement from the bottom right to the top right of
		the window frame, inputting your measurements under <strong
			>'D Height Right'</strong
		>
		on the
		<strong>Measure Form</strong>.
	</p>
</Section>

<Section
	backgroundColor="grey-light"
	id="measure-diagonals"
	class="
   max-md:[&_h3]:justify-self-start max-md:[&_h3]:text-left
   max-md:[&_h4]:justify-self-start max-md:[&_h4]:text-left
   [&_p]:text-left
 "
>
	<h2>Measure Diagonals</h2>
	<h3>E DIAGONAL LEFT</h3>
	<ol class="list-decimal">
		<li>
			<p>
				Place the measuring tape in the bottom left joint of the window and
				align the metal corner (if there is one) just outside of the frame step.
			</p>
		</li>
		<li>
			<p>
				Measure Twice! Until you have at least two measurements within 1/16" of
				each other. Write the measurement under <strong
					>'E Diagonal Left'</strong
				>
				on the <strong>Measure Form.</strong>
			</p>
		</li>
	</ol>
	<div
		class="
    col-span-full! flex w-full flex-col items-center justify-center
    overflow-auto
    sm:flex-row
  "
	>
		<div
			class="
     relative grid w-64 justify-end
     md:w-auto
   "
		>
			<Image
				src={leftRightDiagonalA}
				class="
      w-48 object-contain
      md:w-68
    "
			/>
			<div
				class="
      absolute top-[51%] left-0
      md:left-[-31%] md:w-48
    "
			>
				<div
					class="
       w-full text-[0.67rem] font-bold text-indow-blue
       md:text-sm
     "
				>
					E DIAGONAL LEFT
				</div>
				<div
					class="
       relative h-px w-44 bg-indow-blue
       md:w-60
     "
				>
					<div
						class="
        absolute top-[-50%] right-0 size-3 translate-y-[-50%] transform
        rounded-full bg-inherit
      "
					></div>
				</div>
			</div>
		</div>
		<div
			class="
     relative grid w-64 justify-start
     md:w-auto
   "
		>
			<Image
				src={leftRightDiagonalB}
				class="
      w-48 object-contain
      md:w-68
    "
			/>
			<div
				class="
      absolute top-[51%] -right-2
      md:right-[-31%]
    "
			>
				<div
					class="
       w-full text-right text-[0.67rem] font-bold text-indow-blue
       md:text-sm
     "
				>
					F DIAGONAL RIGHT
				</div>
				<div
					class="
       relative h-px w-40 bg-indow-blue
       md:w-52
     "
				>
					<div
						class="
        absolute top-[-50%] left-0 size-3 translate-y-[-50%] transform
        rounded-full bg-inherit
      "
					></div>
				</div>
			</div>
		</div>
	</div>

	<h3>F Diagonal Right</h3>
	<p>
		Repeat instructions for E Diagonal Left, but with the measure tape going
		from the bottom right joint of the window frame to the top left joint. Write
		the measurement under <strong>'F Diagonal Right'</strong> on the
		<strong>Measure Form</strong>. Try to keep the tape measure taut when
		measuring diagonals.
	</p>
	<h4>NOTE:</h4>
	<p>
		The joint is where the two sides of the window frame meet. When you measure
		from joint to opposite joint, still measure within the frame step where your
		insert will be installed. We recommend two people for larger windows.
	</p>
</Section>

<Section
	id="final-window-check"
	class="
   max-md:[&_h3]:justify-self-start max-md:[&_h3]:text-left
   [&_p]:text-left
 "
>
	<h2>Final Window Check</h2>
	<p>
		Any number of reasons can cause a frame to curve or bow over time. There are
		convex (outward) bows and concave (inward) bows. Checking for bows ensures
		your inserts will fit snugly within the frames.
	</p>
	<h3>CHECK FOR BOWS</h3>
	<p>
		<strong>Convex:</strong> Stretch a string, level, or other straight edge tool
		from corner to corner on one side. Pull it tight to see if there's a point where
		the string (or other tool) isn't touching the frame (see convex diagram). If you
		see a gap, the frame has an outward convex bow. Check all four sides of each window.
	</p>
	<p>
		<strong>Concave:</strong> Stretch the string or other tool across the face of
		the frame to see if any points of the frame dip toward the window's interior or
		pass over the string or other tool (see concave diagram).
	</p>
	<div
		class="
    mb-8 flex flex-col
    sm:flex-row
  "
	>
		<Image src={bowedFrameA} class="w-full object-contain">
			{#snippet descriptionSlot()}
				<p class="text-center! font-bold text-indow-blue!">
					THIS WINDOW HAS AN OUTWARD OR CONVEX BOWED FRAME
				</p>
			{/snippet}
		</Image>
		<Image src={bowedFrameB} class="w-full object-contain">
			{#snippet descriptionSlot()}
				<p class="text-center! font-bold text-indow-blue!">
					THIS WINDOW HAS AN INWARD OR CONCAVE BOWED FRAME
				</p>
			{/snippet}
		</Image>
	</div>

	<p>
		Follow the directions below if you detect a gap thicker than a pencil tip.
		If you detect no bows, continue to the next step. To learn more <a
			target="_blank"
			rel="external"
			href="https://www.youtube.com/watch?v=cuIv-02t0SU">watch our video</a
		> on detecting bowed frames.
	</p>
	<p>
		To measure the thickness of an inward or outward bow in a frame, tape both
		ends of the string to the corners so the string is taut, and use a ruler to
		measure the thickness of the bow where the gap is largest. Alternatively,
		someone can help measure the bow thickness while you hold the string taut.
		If the frame is bowed more than %" (the thickness of a pencil tip) record
		which side of the frame is bowed, the direction (inward or outward) and the
		bow thickness in the <strong>"Notes"</strong> section of the
		<strong>Measure Form</strong>.
	</p>
	<p>
		Be sure to include where on the frame it's thickest and the start and stop
		location of the bow in inches. If the bow is on the bottom or top, provide
		the distance from the left where the gap is largest.
	</p>
	<p>
		<strong>Example:</strong> The frame has a 9/32" thick inward gap on the left side,
		three inches from the bottom left hand corner of the sill.
	</p>

	<Image src={gapValue} class="no-bleed" />
</Section>

<Section
	backgroundColor="grey-light"
	id="submit-measurements"
	class="
   max-md:[&_h3]:justify-self-start max-md:[&_h3]:text-left
   [&_p]:text-left
 "
>
	<h2>Submit Measurements Online</h2>
	<p>
		Check your email for your Measure by Indow link to access the portal.
		Subject line includes <strong>"Measure by Indow"</strong>. Once you're in,
		click <strong>Add Item > Measure Now</strong> and fill out all three
		dropdowns:
		<strong>"Label Insert", "Order Details", "Measure & Save"</strong> one at a time.
	</p>

	<h3>ORDER DETAILS</h3>
	<p>
		<strong>Notes:</strong> If you found bows, add description here. Include which
		side is bowed. the direction (inward/concave or outward/convex) as well as the
		thickness of the bow.
	</p>
	<h3>MEASURE & SAVE</h3>
	<p>
		<strong>Shape:</strong> Select "Rectangle" unless you are working with a
		special shape such as an arch or octagon. For custom-shaped windows, please
		refer to the <strong>Custom Order Form</strong>. Enter all measurements as
		they appear on your <strong>Measure Form</strong>, selecting the larger
		measurement of the two within 1/16" of each other. Type the whole number in
		the field provided, the top number of the fraction in the field provided,
		and select the bottom part of the fraction from the dropdown. Do not enter
		any additional symbols or spaces.
	</p>
	<p>See example:</p>

	<Image src={measureForm} />

	<h3>REVIEW YOUR INFORMATION</h3>
	<p>
		Watch out for typos! If you are having trouble resolving any errors, select
		the <strong>"schedule a support call"</strong> option. If all errors are
		resolved, click <strong>"Submit Values"</strong>. Once we receive your
		measurements, our team will recheck them. When the assessment is complete
		and any issues have been resolved, we'll send you a final invoice. You'll
		have a chance to review your order before making the final payment. If you
		feel any items in your order need attention, contact your
		<strong>Fit Specialist</strong> before you proceed. Once you confirm your order
		for the final time, you're finished!
	</p>
</Section>
